import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { STORAGE_KEYS } from '../../constants';
import {
  getAllWorkspaces,
  getWorkspaceById,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  switchWorkspace,
  removeUserFromWorkspace,
  updateWorkspaceSettings,
  archiveWorkspace as archiveWorkspaceService,
} from '../../services/workspaceService/workspaceService';
import {
  getAllWorkspacesFailure,
  getAllWorkspacesSuccess,
  getAdminWorkspacesFailure,
  getAdminWorkspacesSuccess,
  getArchivedWorkspacesFailure,
  getArchivedWorkspacesSuccess,
  getWorkspaceByIdFailure,
  getWorkspaceByIdSuccess,
  createWorkspaceFailure,
  createWorkspaceSuccess,
  updateWorkspaceFailure,
  updateWorkspaceSuccess,
  deleteWorkspaceFailure,
  deleteWorkspaceSuccess,
  switchWorkspaceRequest,
  switchWorkspaceFailure,
  switchWorkspaceSuccess,
  removeUserFromWorkspaceFailure,
  removeUserFromWorkspaceSuccess,
  getWorkspaceByIdRequest,
  updateWorkspaceSettingsSuccess,
  updateWorkspaceSettingsFailure,
  archiveWorkspaceFailure,
  archiveWorkspaceSuccess,
  getArchivedWorkspacesRequest,
  getAdminWorkspacesRequest,
} from './workspace.action';
import { WorkspaceActionTypes } from './workspace.types';
import {
  getUserDataRequest,
  getWorkspaceDataRequest,
  getWorkspaceDataSuccess,
} from '../user/user.action';
import { RootState } from '../rootReducer';
import { updateToken } from '../../services/authService/authService';

// Define action interfaces for saga parameters
interface GetAllWorkspacesAction {
  type: WorkspaceActionTypes.GET_ALL_WORKSPACES_REQUEST;
}

interface GetAdminWorkspacesAction {
  type: WorkspaceActionTypes.GET_ADMIN_WORKSPACES_REQUEST;
  payload?: { isAdmin?: boolean };
}

interface GetArchivedWorkspacesAction {
  type: WorkspaceActionTypes.GET_ARCHIVED_WORKSPACES_REQUEST;
}

interface ArchiveWorkspaceAction {
  type: WorkspaceActionTypes.ARCHIVE_WORKSPACE_REQUEST;
  payload: string;
}

interface GetWorkspaceByIdAction {
  type: WorkspaceActionTypes.GET_WORKSPACE_BY_ID_REQUEST;
  payload: string;
}

interface CreateWorkspaceAction {
  type: WorkspaceActionTypes.CREATE_WORKSPACE_REQUEST;
  payload: { name: string };
}

interface UpdateWorkspaceAction {
  type: WorkspaceActionTypes.UPDATE_WORKSPACE_REQUEST;
  payload: { name: string };
}

interface DeleteWorkspaceAction {
  type: WorkspaceActionTypes.DELETE_WORKSPACE_REQUEST;
  payload: string;
}

interface SwitchWorkspaceAction {
  type: WorkspaceActionTypes.SWITCH_WORKSPACE_REQUEST;
  payload: string;
}

interface RemoveUserFromWorkspaceAction {
  type: WorkspaceActionTypes.REMOVE_USER_FROM_WORKSPACE_REQUEST;
  payload: { userId: string };
}

interface UpdateWorkspaceSettingsAction {
  type: WorkspaceActionTypes.UPDATE_WORKSPACE_SETTINGS_REQUEST;
  payload: {
    duplicateEmailEnabled: boolean;
  };
}

// Saga worker functions
function* getAllWorkspacesSaga(action: GetAllWorkspacesAction): SagaIterator {
  try {
    const response = yield call(getAllWorkspaces);

    // Handle different response structures
    let workspaces: any[];
    if (response.workspaces) {
      workspaces = response.workspaces;
    } else if (response.data) {
      workspaces = response.data;
    } else if (Array.isArray(response)) {
      workspaces = response;
    } else {
      workspaces = [];
    }

    yield put(getAllWorkspacesSuccess(workspaces));
  } catch (error: any) {
    console.error('Error fetching all workspaces:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch workspaces';
    yield put(getAllWorkspacesFailure(errorMessage));
  }
}

function* getAdminWorkspacesSaga(
  action: GetAdminWorkspacesAction
): SagaIterator {
  try {
    const response = yield call(getAllWorkspaces, action.payload);

    // Handle different response structures
    let workspaces: any[];
    if (response.workspaces) {
      workspaces = response.workspaces;
    } else if (response.data) {
      workspaces = response.data;
    } else if (Array.isArray(response)) {
      workspaces = response;
    } else {
      workspaces = [];
    }

    yield put(getAdminWorkspacesSuccess(workspaces));
  } catch (error: any) {
    console.error('Error fetching admin workspaces:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch admin workspaces';
    yield put(getAdminWorkspacesFailure(errorMessage));
  }
}

function* getArchivedWorkspacesSaga(
  action: GetArchivedWorkspacesAction
): SagaIterator {
  try {
    const response = yield call(getAllWorkspaces, {
      isAdmin: true,
      archive: true,
    });

    let workspaces: any[];
    if (response.workspaces) {
      workspaces = response.workspaces;
    } else if (response.data) {
      workspaces = response.data;
    } else if (Array.isArray(response)) {
      workspaces = response;
    } else {
      workspaces = [];
    }

    yield put(getArchivedWorkspacesSuccess(workspaces));
  } catch (error: any) {
    console.error('Error fetching archived workspaces:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch archived workspaces';
    yield put(getArchivedWorkspacesFailure(errorMessage));
  }
}

function* archiveWorkspaceSaga(action: ArchiveWorkspaceAction): SagaIterator {
  try {
    yield call(archiveWorkspaceService, action.payload);
    yield put(archiveWorkspaceSuccess(action.payload));
    yield put(getArchivedWorkspacesRequest());
    yield put(getAdminWorkspacesRequest({ isAdmin: true }));
  } catch (error: any) {
    console.error('Error archiving workspace:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to archive workspace';
    yield put(archiveWorkspaceFailure(errorMessage));
  }
}

function* getWorkspaceByIdSaga(action: GetWorkspaceByIdAction): SagaIterator {
  try {
    const response = yield call(getWorkspaceById);

    // Handle different response structures
    let workspace: any;
    if (response.workspace) {
      workspace = response.workspace;
    } else if (response.data) {
      workspace = response.data;
    } else {
      workspace = response;
    }

    yield put(getWorkspaceByIdSuccess(workspace));
  } catch (error: any) {
    console.error('Error fetching workspace by ID:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch workspace';
    yield put(getWorkspaceByIdFailure(errorMessage));
  }
}

function* createWorkspaceSaga(action: CreateWorkspaceAction): SagaIterator {
  try {
    const response = yield call(createWorkspace, action.payload);

    // Handle different response structures
    let workspace: any;
    if (response.workspace) {
      workspace = response.workspace;
    } else if (response.data) {
      workspace = response.data;
    } else {
      workspace = response;
    }

    yield put(createWorkspaceSuccess(workspace));

    // Automatically switch to the newly created workspace
    if (workspace._id) {
      yield put(switchWorkspaceRequest(workspace._id));
    }
  } catch (error: any) {
    console.error('Error creating workspace:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to create workspace';
    yield put(createWorkspaceFailure(errorMessage));
  }
}

function* updateWorkspaceSaga(action: UpdateWorkspaceAction): SagaIterator {
  try {
    const response = yield call(updateWorkspace, action.payload);

    // Handle different response structures
    let workspace: any;
    if (response.workspace) {
      workspace = response.workspace;
    } else if (response.data) {
      workspace = response.data;
    } else if (response.name) {
      workspace = { name: response.name };
    } else {
      workspace = { name: action.payload.name };
    }

    yield put(updateWorkspaceSuccess(workspace));
    yield put(getWorkspaceByIdRequest());

    const userWorkspace: { workspace?: Record<string, unknown> } | null =
      yield select((state: RootState) => state.user.workspace);

    if (userWorkspace?.workspace) {
      const updatedUserWorkspace = {
        ...userWorkspace,
        workspace: { ...userWorkspace.workspace, ...workspace },
      };
      localStorage.setItem(
        STORAGE_KEYS.WORKSPACE,
        JSON.stringify(updatedUserWorkspace)
      );
      yield put(getWorkspaceDataSuccess(updatedUserWorkspace));
    } else {
      yield put(getWorkspaceDataRequest());
    }
  } catch (error: any) {
    console.error('Error updating workspace:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update workspace';
    yield put(updateWorkspaceFailure(errorMessage));
  }
}

function* deleteWorkspaceSaga(action: DeleteWorkspaceAction): SagaIterator {
  try {
    yield call(deleteWorkspace, action.payload);
    yield put(deleteWorkspaceSuccess(action.payload));
  } catch (error: any) {
    console.error('Error deleting workspace:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to delete workspace';
    yield put(deleteWorkspaceFailure(errorMessage));
  }
}

function* switchWorkspaceSaga(action: SwitchWorkspaceAction): SagaIterator {
  try {
    const response = yield call(switchWorkspace, action.payload);

    // Handle different response structures
    let token: string;
    if (response.data && response.data.token) {
      token = response.data.token;
    } else if (response.token) {
      token = response.token;
    } else {
      throw new Error('No token received from switch workspace response');
    }

    // Store the new token in localStorage
    updateToken(token);
    yield put(getWorkspaceByIdRequest());
    yield put(getUserDataRequest());
    yield put(getWorkspaceDataRequest());
    yield put(switchWorkspaceSuccess(action.payload));
  } catch (error: any) {
    console.error('Error switching workspace:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to switch workspace';
    yield put(switchWorkspaceFailure(errorMessage));
  }
}

function* removeUserFromWorkspaceSaga(
  action: RemoveUserFromWorkspaceAction
): SagaIterator {
  try {
    const response = yield call(removeUserFromWorkspace, action.payload.userId);

    // Handle different response structures
    let message: string;
    if (response.message) {
      message = response.message;
    } else if (response.data && response.data.message) {
      message = response.data.message;
    } else {
      message = 'User removed successfully';
    }

    yield put(removeUserFromWorkspaceSuccess(message));
    yield put(getWorkspaceByIdRequest());
  } catch (error: any) {
    console.error('Error removing user from workspace:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Failed to remove user';
    yield put(removeUserFromWorkspaceFailure(errorMessage));
  }
}

function* updateWorkspaceSettingsSaga(
  action: UpdateWorkspaceSettingsAction
): SagaIterator {
  try {
    const response = yield call(updateWorkspaceSettings, action.payload);

    // Handle different response structures
    let message: string;
    if (response.message) {
      message = response.message;
    } else if (response.data && response.data.message) {
      message = response.data.message;
    } else {
      message = 'Workspace settings updated successfully';
    }

    yield put(
      updateWorkspaceSettingsSuccess({
        message,
        duplicateEmailEnabled: action.payload.duplicateEmailEnabled,
      })
    );
  } catch (error: any) {
    console.error('Error updating workspace settings:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update workspace settings';
    yield put(updateWorkspaceSettingsFailure(errorMessage));
  }
}

// Watcher sagas
function* watchGetAllWorkspacesSaga(): SagaIterator {
  yield takeLatest(
    WorkspaceActionTypes.GET_ALL_WORKSPACES_REQUEST,
    getAllWorkspacesSaga
  );
}

function* watchGetAdminWorkspacesSaga(): SagaIterator {
  yield takeLatest(
    WorkspaceActionTypes.GET_ADMIN_WORKSPACES_REQUEST,
    getAdminWorkspacesSaga
  );
}

function* watchGetArchivedWorkspacesSaga(): SagaIterator {
  yield takeLatest(
    WorkspaceActionTypes.GET_ARCHIVED_WORKSPACES_REQUEST,
    getArchivedWorkspacesSaga
  );
}

function* watchArchiveWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.ARCHIVE_WORKSPACE_REQUEST,
    archiveWorkspaceSaga
  );
}

function* watchGetWorkspaceByIdSaga(): SagaIterator {
  yield takeLatest(
    WorkspaceActionTypes.GET_WORKSPACE_BY_ID_REQUEST,
    getWorkspaceByIdSaga
  );
}

function* watchCreateWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.CREATE_WORKSPACE_REQUEST,
    createWorkspaceSaga
  );
}

function* watchUpdateWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.UPDATE_WORKSPACE_REQUEST,
    updateWorkspaceSaga
  );
}

function* watchDeleteWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.DELETE_WORKSPACE_REQUEST,
    deleteWorkspaceSaga
  );
}

function* watchSwitchWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.SWITCH_WORKSPACE_REQUEST,
    switchWorkspaceSaga
  );
}

function* watchRemoveUserFromWorkspaceSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.REMOVE_USER_FROM_WORKSPACE_REQUEST,
    removeUserFromWorkspaceSaga
  );
}

function* watchUpdateWorkspaceSettingsSaga(): SagaIterator {
  yield takeEvery(
    WorkspaceActionTypes.UPDATE_WORKSPACE_SETTINGS_REQUEST,
    updateWorkspaceSettingsSaga
  );
}

// Root workspace saga
export function* workspaceSaga(): SagaIterator {
  yield all([
    fork(watchGetAllWorkspacesSaga),
    fork(watchGetAdminWorkspacesSaga),
    fork(watchGetArchivedWorkspacesSaga),
    fork(watchArchiveWorkspaceSaga),
    fork(watchGetWorkspaceByIdSaga),
    fork(watchCreateWorkspaceSaga),
    fork(watchUpdateWorkspaceSaga),
    fork(watchDeleteWorkspaceSaga),
    fork(watchSwitchWorkspaceSaga),
    fork(watchRemoveUserFromWorkspaceSaga),
    fork(watchUpdateWorkspaceSettingsSaga),
  ]);
}

export default workspaceSaga;
